import { useEffect, useState } from 'react'
import useUser from '../../../hooks/use-user'
import LookupField from '../../wells/lookup-field'
import { WellDetailsComponentBase } from './well-details-types'
import { can } from '../../../services/user-utils'
import { Permissions } from '../../../../types/user-types'
import { isCouncilUserOrAdmin, isDriller } from '../../../services/user-utils'
import { CheckableField, CheckableFields, FormField, Heading } from '@msaf/core-react'

interface WellDetailsAssignWellProps extends WellDetailsComponentBase {
  selectedCompany: any
  newWellCouncilId?: string
}

export default function WellDetailsAssignWell({
  isLoading,
  setValue,
  validationErrors,
  fields,
  selectedCompany,
  newWellCouncilId,
}: WellDetailsAssignWellProps) {
  const { user } = useUser()
  const [assignedEntityTypeCouncil, toggleAssignedEntityType] = useState(true)
  const [companyAssignmentGranted, setCompanyAssignmentGranted] = useState<boolean>(false)

  useEffect(() => {
    if (fields.council == null) {
      setCompanyAssignmentGranted(false)
      return
    }

    const hasCompanyToAssign =
      !!selectedCompany &&
      Array.isArray(selectedCompany.filters) &&
      selectedCompany.filters.includes(String(fields.council))

    setCompanyAssignmentGranted(hasCompanyToAssign)
  }, [selectedCompany, fields.council])

  const fieldFilters = () => {
    let filters: Record<string, string | number | boolean> = {}
    if (assignedEntityTypeCouncil) {
      // Council users must be filtered by permissions
      filters = { permission: Permissions.CREATE_WELL, readOnlyField: false }
    } else {
      // Assigning to drillers requires a company to get the drillers from
      filters = { companyId: fields.drillingCompany ?? '' }
    }

    // SysAdmins need to define which council to lookup
    if (can(user, Permissions.MAINTAIN_ALL_USERS) && fields.council) {
      filters.councilId = fields.council ?? newWellCouncilId
    }
    return filters
  }

  return (
    <>
      <Heading isSkeleton={isLoading} level={2}>
        Assign Well
      </Heading>
      {user && isCouncilUserOrAdmin(user) ? (
        <FormField
          toolTipMessage={
            !assignedEntityTypeCouncil
              ? 'This will assign the record to the selected drilling company staff member to complete'
              : ''
          }
          isSkeleton={isLoading}
          labelId='assignTo'
          label='Assign to'
        >
          <CheckableFields ariaLabelledBy='assignTo' isHorizontal>
            <CheckableField
              id='councilStaff'
              name='councilStaff'
              label='Council staff'
              isSkeleton={isLoading}
              describedBy='councilStaff'
              type='radio'
              checked={assignedEntityTypeCouncil}
              onChange={() => toggleAssignedEntityType(!assignedEntityTypeCouncil)}
            />
            <CheckableField
              id='drillingCompany'
              name='drillingCompany'
              label='Drilling company staff'
              isSkeleton={isLoading}
              describedBy='drillingCompany'
              type='radio'
              checked={!assignedEntityTypeCouncil}
              onChange={() => toggleAssignedEntityType(!assignedEntityTypeCouncil)}
            />
          </CheckableFields>
        </FormField>
      ) : (
        <FormField isSkeleton={isLoading} labelId='assignTo' label='Assign to'>
          <CheckableFields ariaLabelledBy='assignTo' isHorizontal>
            <CheckableField
              id='drillingCompany'
              name='drillingCompany'
              label='Drilling company staff'
              isSkeleton={isLoading}
              type='radio'
              checked
              disabled
            />
          </CheckableFields>
        </FormField>
      )}

      {!assignedEntityTypeCouncil && !companyAssignmentGranted && (
        <p>
          Please select a drilling company in the field above to generate a list of staff to assign the well record to
        </p>
      )}
      {user && isCouncilUserOrAdmin(user) && (
        <FormField
          isSkeleton={isLoading}
          label='Assigned User'
          labelId='councilUsers'
          validationMessages={validationErrors?.assignedUser}
          isRequired
        >
          <LookupField
            isSkeleton={isLoading}
            labelledBy='assignedUser'
            fieldId={assignedEntityTypeCouncil ? 'councilStaff' : 'drillerStaff'}
            fieldFilters={fieldFilters()}
            value={`${fields.assignedUser ?? ''}`}
            setValue={({ value }) => setValue('assignedUser', value)}
            isInvalid={!!validationErrors?.assignedUser}
          />
        </FormField>
      )}
      {user && isDriller(user) && (
        <FormField
          isSkeleton={isLoading}
          label='Assigned to'
          labelId='councilUsers'
          validationMessages={validationErrors?.assignedUser}
          isRequired
        >
          <LookupField
            isSkeleton={isLoading}
            labelledBy='assignedUser'
            fieldId={'drillerStaff'}
            fieldFilters={{ councilId: fields.council ?? '' }}
            value={`${fields.assignedUser ?? ''}`}
            setValue={({ value }) => setValue('assignedUser', value)}
            isInvalid={!!validationErrors?.assignedUser}
          />
        </FormField>
      )}
    </>
  )
}
